import { useCallback, memo } from "react";
import "../styles.css";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Archive, MessageCircle } from "lucide-react";
import { Tooltip } from "@mui/material";
import { MenuItemButton } from "@/components/src/menu-item-button/menu-item-button.component";
import {
	ContextMenu,
	ContextMenuTrigger,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator,
} from "@/dentlab/src/components/ui/context-menu";
import { useJobStore } from "../../../store/Jobs";
import { usePatientStore } from "../../../store/Patient";
import { JobStatusEnum } from "@/lib/types/job";
import { JobRequestChannel } from "@/lib/types/job-list.types";
import { supabase } from "@/lib/supabase";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import {
	useDeleteJob,
	DeleteJobMenuTrigger,
} from "./delete-job-menu-item.component";

interface JobListItemProps {
	basePath: "/client" | "/vendor";
	entityId: string;
	jobId: number;
	jobStatus: JobStatusEnum;
	jobTitle: string;
	jobCode: string;
	patientId: string;
	sharedWith?: boolean;
	isSelected: boolean;
	onRefresh?: () => void;
}

const JobListItemBase: React.FC<JobListItemProps> = ({
	basePath,
	entityId,
	jobId,
	jobStatus,
	jobTitle,
	jobCode,
	patientId,
	sharedWith,
	isSelected,
	onRefresh,
}) => {
	const navigate = useNavigate();

	const handleClick = useCallback(() => {
		navigate(`${basePath}/${entityId}/job/${jobId}`);
	}, [basePath, entityId, jobId, navigate]);

	const handleArchiveJob = useCallback(async () => {
		const { data } = await supabase.rpc("archive_job", {
			p_job_id: jobId,
		});

		if (!data) {
			showNotification({
				message: "Ein Fehler ist aufgetreten",
				type: "error",
			});
			return;
		}

		const result = data[0];

		if (!(result as any).success) {
			showNotification({
				message: (result as any).message,
				type: "error",
			});
			return;
		}

		const { fetchJobsByStatus, handleJobRouteChange } = useJobStore(
			(state) => ({
				fetchJobsByStatus: state.fetchJobsByStatus,
				handleJobRouteChange: state.handleJobRouteChange,
			})
		);
		await fetchJobsByStatus(jobStatus);
		await handleJobRouteChange(String(jobId), true);

		showNotification({
			message: "Der Auftrag wurde archiviert",
			type: "success",
		});
	}, [jobId, jobStatus]);

	const handleNewWindow = useCallback(() => {
		window.open(
			`${window.location.origin}${basePath}/${entityId}/job/${jobId}`,
			"_blank"
		);
	}, [basePath, entityId, jobId]);

	const handleDeleted = useCallback(() => {
		onRefresh?.();
	}, [onRefresh]);

	const { handleDeleteSelect, deleteDialogs } = useDeleteJob(
		jobId,
		handleDeleted
	);

	return (
		<>
			<ContextMenu>
				<ContextMenuTrigger asChild>
					<div data-testid={`job-list-item-${jobId}`}>
						<MenuItemButton
							onClick={handleClick}
							selected={isSelected}
						>
							<JobListItemContent
								jobTitle={jobTitle}
								jobCode={jobCode}
								patientId={patientId}
							/>
							{sharedWith && (
								<Tooltip
									title="Der Auftrag ist mit dem Auftraggeber geteilt."
									placement="right"
								>
									<MessageCircle className="w-4 h-4" />
								</Tooltip>
							)}
						</MenuItemButton>
					</div>
				</ContextMenuTrigger>
				<ContextMenuContent>
					<ContextMenuItem
						onSelect={handleNewWindow}
						data-testid="job-context-menu-new-window"
						aria-label="In neuem Fenster öffnen"
					>
						<ExternalLink className="size-4" />
						In neuem Fenster öffnen
					</ContextMenuItem>
					<ContextMenuItem
						onSelect={handleArchiveJob}
						data-testid="job-context-menu-archive"
						aria-label="Auftrag archivieren"
					>
						<Archive className="size-4" />
						Archivieren
					</ContextMenuItem>
					<ContextMenuSeparator />
					<DeleteJobMenuTrigger onSelect={handleDeleteSelect} />
				</ContextMenuContent>
			</ContextMenu>
			{deleteDialogs}
		</>
	);
};

export const JobListItem = memo(JobListItemBase);

const JobListItemContentBase: React.FC<{
	jobTitle: string;
	jobCode: string;
	patientId: string;
}> = ({ jobTitle, jobCode, patientId }) => {
	const { patientName, patientsLookup } = usePatientStore((state) => ({
		patientName: state.patientName,
		patientsLookup: state.patientsLookup,
	}));
	return (
		<div>
			<div className="flex flex-col gap-0.5 text-sm">
				<p className="font-semibold">
					{jobCode}
					{patientId && " "}
					{patientId && patientName(patientsLookup[patientId])}
				</p>
				<p className="italic">{jobTitle}</p>
			</div>
		</div>
	);
};

export const JobListItemContent = memo(JobListItemContentBase);

export const ChannelListItem: React.FC<{
	channelId: string;
	entityId: string;
	basePath: "/client" | "/vendor";
	children: React.ReactNode;
}> = ({ channelId, entityId, basePath, children }) => {
	const navigate = useNavigate();

	const handleClick = useCallback(() => {
		navigate(`${basePath}/${entityId}/job/request/${channelId}`);
	}, [basePath, channelId, entityId, navigate]);

	return (
		<MenuItemButton
			onClick={handleClick}
			selected={location.pathname.includes(`/job/request/${channelId}`)}
		>
			{children}
		</MenuItemButton>
	);
};

const ChannelListItemContentBase: React.FC<{
	channel: JobRequestChannel;
}> = ({ channel }) => {
	return (
		<div className="flex flex-col gap-0.5 text-sm">
			<div className="flex items-baseline justify-between gap-2">
				<p>
					<b>Neue Auftragsanfrage</b>
				</p>
				{channel.created_at && (
					<span className="text-xs text-gray-500 shrink-0">
						{new Date(channel.created_at).toLocaleDateString(
							"de-DE",
							{
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							}
						)}
					</span>
				)}
			</div>
			<p className="italic">{channel.title ?? "Auftragsanfrage"}</p>
			{channel.unreadCount > 0 && (
				<span className="text-xs text-blue-600">
					{channel.unreadCount} ungelesen
				</span>
			)}
		</div>
	);
};

export const ChannelListItemContent = memo(ChannelListItemContentBase);
